import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BlackbaudRaisersEdgeIntegration as BREIntegration,
  IBlackbaudContactMapping,
} from '@feathr/blackbox';
import { Button, CardV2 as Card, Heading, Spinner, Text, Toggle } from '@feathr/components';

import SyncMappingCard from '../../SyncMappingCard';
import DeleteMappingButton from '../DeleteMappingButton';
import type { TMappingDisabledReason } from '../DeleteMappingButton/';
import RaisersEdgeDateLastSynced from '../RaisersEdgeDateLastSynced';
import { patchRaisersEdgeSetting } from '../RaisersEdgeSyncWizard.utils';
import SyncPersonSelect from './SyncPersonSelect';

import * as styles from './RaisersEdgeSyncPerson.css';

interface IRaisersEdgeSyncPersonProps {
  addMappingButton: JSX.Element;
  integration: BREIntegration;
}

function RaisersEdgeSyncPerson({
  addMappingButton,
  integration,
}: IRaisersEdgeSyncPersonProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  // The wizard owns the load; this reads the result.
  const { contactMappingStore } = integration;
  const { error, isFetched, isPending, sortedMappings: mappings } = contactMappingStore;

  const downsyncActivities = integration.get('downsync_activities');

  async function handleToggleConstituentSync(syncValue: boolean): Promise<void> {
    await patchRaisersEdgeSetting({
      changes: { downsync_activities: { ...downsyncActivities, constituents: syncValue } },
      integration,
      successMessage: syncValue ? t('Enabled constituent sync.') : t('Disabled constituent sync.'),
      t,
    });
  }

  function handleToggleChange(): void {
    void handleToggleConstituentSync(!downsyncActivities?.constituents);
  }

  function handleRetryLoad(): void {
    void contactMappingStore.fetch();
  }

  const noMappingsMessage: JSX.Element = (
    <div className={styles.message}>
      <h4>{t('No mappings found')}</h4>
      <p>{t('Add a new mapping to get started.')}</p>
      {addMappingButton}
    </div>
  );

  const loadErrorMessage: JSX.Element = (
    <Stack className={styles.message} gap={theme.spacing['1']}>
      <Heading order={4} variant={'h4'}>
        {t("Couldn't load mappings")}
      </Heading>
      <Text.DefaultRegular>
        {t('Try again. If the problem continues, reload the page.')}
      </Text.DefaultRegular>
      <Button onClick={handleRetryLoad} type={'primary'}>
        {t('Try again')}
      </Button>
    </Stack>
  );

  // Determine the reason a mapping is disabled.
  const getDisabledReason = (
    mapping: IBlackbaudContactMapping,
  ): TMappingDisabledReason | undefined => {
    // If a mapping is required, it cannot be deleted.
    if (mapping.required) {
      return 'required';
    }
    // If a mapping is being provisioned, it cannot be deleted.
    if (mapping.has_provisioning_subscriptions) {
      return 'provisioning';
    }
    return undefined;
  };

  const constituentSyncCard: JSX.Element = (
    <Card contentClassName={styles.syncCard} name={'constituent-sync-card'}>
      <Card.Content contentClassName={styles.syncCardContent}>
        <div className={styles.syncCardTextContent}>
          <div className={styles.syncCardTitle}>
            {t("Sync constituents from Raiser's Edge NXT to Feathr")}
          </div>
          <div className={styles.syncCardDescription}>
            {t(
              'When enabled, constituent create/update events in RE NXT trigger person record updates in Feathr according to mapped fields.',
            )}
          </div>
        </div>
        <Toggle
          disabled={integration.isUpdating}
          onChange={handleToggleChange}
          value={downsyncActivities?.constituents ?? false}
        />
      </Card.Content>
    </Card>
  );

  if (!isFetched) {
    // Before the first load settles, and while a retry runs, there is nothing to show yet.
    if (isPending || !error) {
      return <Spinner />;
    }
    return (
      <>
        {constituentSyncCard}
        {loadErrorMessage}
      </>
    );
  }

  if (mappings.length) {
    const mappingCards: JSX.Element[] = mappings.map((mapping) => (
      <SyncMappingCard
        controls={[
          <RaisersEdgeDateLastSynced
            className={styles.sync}
            dateLastSynced={mapping.date_last_synced}
            key={`${mapping.id}-date-last-synced`}
            showLastSyncedPrefix={false}
            showNeverSynced={true}
          />,
          <DeleteMappingButton
            disabled={mapping.required || mapping.has_provisioning_subscriptions}
            disabledReason={getDisabledReason(mapping)}
            id={mapping.id}
            integration={integration}
            key={`${mapping.id}-delete-mapping-button`}
          />,
        ]}
        key={mapping.id}
        mapping={mapping}
        mappings={mappings}
        target={
          <SyncPersonSelect
            className={styles.select}
            integration={integration}
            mapping={mapping}
            mappings={mappings}
          />
        }
      />
    ));
    return (
      <>
        {constituentSyncCard}
        {mappingCards}
      </>
    );
  }

  return (
    <>
      {constituentSyncCard}
      {noMappingsMessage}
    </>
  );
}

export default observer(RaisersEdgeSyncPerson);
